import { useEffect, useMemo, useRef } from 'react';

import { AppDashboard, type CtaIcon as CtaIconProperties } from '@shared/types/app';
import dayjs from 'dayjs';
import { orderBy } from 'lodash';
import Markdown from 'markdown-to-jsx';

import {
  useCurrentEntertainerQuery,
  useEntertainerByBookingHandleQuery,
} from '@queries/EntertainerQueries';
import { FeedEvent } from '@queries/EventQueries';
import { useCurrentUser, useUserWalletQuery } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { useIsVisible } from '@hooks/useIsVisible';
import { useShareLink } from '@hooks/useShareLink';

import { useRouter } from '@router/index';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';

type PrimaryEngagementCardProperties = NonNullable<AppDashboard['primaryEngagementCard']>;

const CtaIcon = ({ icon }: { icon: CtaIconProperties }) => {
  if (typeof icon === 'string') {
    return <Markdown>{icon}</Markdown>;
  }

  return <FontAwesomeIcon {...icon.faProps} />;
};

const Dots = ({
  completedCount,
  totalCount = 2,
}: {
  completedCount: number;
  totalCount?: number;
}) => {
  return (
    <div className="d-flex align-items-center gap-1">
      {Array.from({ length: totalCount }).map((_, index) => (
        <div
          key={index}
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: index < completedCount ? 'var(--color-yellow)' : 'transparent',
            border:
              index < completedCount ? '1.5px dashed var(--color-yellow)' : '1.5px dashed #8a7433',
          }}
        />
      ))}
    </div>
  );
};

const CardListItem = ({
  listItem,
}: {
  listItem: NonNullable<PrimaryEngagementCardProperties['listItems']>[number];
}) => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();

  const { shareLink, didCopy } = useShareLink();

  const handleCopyLinkClick = () => {
    if (listItem.cta?.copyUrl) {
      track('Click engagement section list item copy link');
      shareLink({ url: listItem.cta.copyUrl });
    }
  };

  const handleCtaClick = () => {
    if (listItem.cta?.to) {
      track('Click engagement section list item cta');
      navigate(listItem.cta.to);
    }
  };

  return (
    <div className="py-2" style={{ borderTop: '1px solid rgba(255,255,255,.2)' }}>
      <div className="d-flex gap-2 align-items-center">
        <div
          style={{
            width: 40,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0.08,
              color: 'var(--color-yellow)',
              textTransform: 'uppercase',
            }}
          >
            {dayjs(listItem.startDate).format('MMM')}
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            {dayjs(listItem.startDate).format('D')}
          </div>
          <div
            style={{
              fontSize: 10,
              color: '#cfc6ad',
            }}
          >
            {dayjs(listItem.startDate).format('ddd')}
          </div>
        </div>
        <div className="d-grid w-100">
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {listItem.title ?? 'Details not provided'}
          </div>
          {listItem.dots && (
            <div className="d-flex align-items-center gap-1">
              <div className="me-1">
                <Dots
                  completedCount={listItem.dots.completedCount}
                  totalCount={listItem.dots.totalCount}
                />
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-yellow)' }}>
                {listItem.dots.text}
              </div>
            </div>
          )}
          {listItem.description && (
            <div style={{ fontSize: 12, color: '#cfc6ad' }}>
              <Markdown options={{ forceInline: true }}>{listItem.description}</Markdown>
            </div>
          )}
        </div>
        <div>
          {listItem.cta.copyUrl ? (
            <Button
              color="yellow"
              roundness="rounded"
              size="sm"
              className="px-2"
              endIcon={<FontAwesomeIcon name="share-square" />}
              onClick={handleCopyLinkClick}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{didCopy ? 'Copied' : listItem.cta.text}</span>
            </Button>
          ) : listItem.cta.to ? (
            <Button
              color="yellow"
              variant="outlined"
              roundness="rounded"
              size="sm"
              className="px-2"
              startIcon={listItem.cta.startIcon && <CtaIcon icon={listItem.cta.startIcon} />}
              endIcon={listItem.cta.endIcon && <CtaIcon icon={listItem.cta.endIcon} />}
              onClick={handleCtaClick}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{listItem.cta.text}</span>
            </Button>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-yellow)' }}
            >
              {listItem.cta.startIcon && (
                <div>
                  <CtaIcon icon={listItem.cta.startIcon} />
                </div>
              )}
              <span style={{ whiteSpace: 'nowrap' }}>{listItem.cta.text}</span>
              {listItem.cta.endIcon && (
                <div>
                  <CtaIcon icon={listItem.cta.endIcon} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PrimaryEngagementCardContent = ({
  primaryEngagementCard,
}: {
  primaryEngagementCard: PrimaryEngagementCardProperties;
}) => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();
  const { content, listItems, primaryCta, secondaryCta } = primaryEngagementCard;

  const handlePrimaryContentCtaClick = () => {
    if (primaryCta) {
      track('Click primary engagement section primary cta');
      navigate(primaryCta.to);
    }
  };

  const handleSecondaryContentCtaClick = () => {
    if (secondaryCta) {
      track('Click primary engagement section secondary cta');
      navigate(secondaryCta.to);
    }
  };

  return (
    <div className="mt-2">
      <div className="d-flex gap-3 align-items-center">
        <div>
          <div style={{ fontSize: 21, lineHeight: 1.2 }} className="mb-1">
            <strong>
              <Markdown options={{ forceInline: true }}>{content?.title ?? ''}</Markdown>
            </strong>
          </div>
          <div style={{ color: '#999', fontSize: 13 }}>
            <Markdown options={{ forceInline: true }}>{content?.description ?? ''}</Markdown>
          </div>
        </div>
      </div>
      <div
        className="d-grid mt-3"
        style={{
          borderBottom: '1px solid rgba(255,255,255,.2)',
        }}
      >
        {listItems?.map((listItem) => (
          <CardListItem key={`${listItem.startDate}-${listItem.title}`} listItem={listItem} />
        ))}
      </div>
      <div className="mt-3">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            {primaryCta && (
              <div className="d-inline-block">
                <Button
                  color="yellow"
                  size="sm"
                  className="py-2 px-3"
                  startIcon={primaryCta.startIcon && <CtaIcon icon={primaryCta.startIcon} />}
                  endIcon={primaryCta.endIcon && <CtaIcon icon={primaryCta.endIcon} />}
                  onClick={handlePrimaryContentCtaClick}
                >
                  {primaryCta.text}
                </Button>
              </div>
            )}
          </div>

          <div className="ml-auto">
            {secondaryCta && (
              <Button
                color="yellow"
                variant="flat"
                size="sm"
                className="py-2 px-3"
                startIcon={secondaryCta.startIcon && <CtaIcon icon={secondaryCta.startIcon} />}
                endIcon={secondaryCta.endIcon && <CtaIcon icon={secondaryCta.endIcon} />}
                onClick={handleSecondaryContentCtaClick}
              >
                {secondaryCta.text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PrimaryEngagementCard = ({
  primaryEngagementCard,
}: {
  primaryEngagementCard: NonNullable<AppDashboard['primaryEngagementCard']>;
}) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const { walletIsReady, wallet } = useUserWalletQuery();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIsVisible(containerRef);
  const {
    refetchEntertainer,
    entertainer: currentEntertainer,
    upcomingEvents,
    upcomingScheduleDateDetails,
    entertainerEventReferralOpportunities,
  } = useCurrentEntertainerQuery({
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const handlePrimaryTitleCtaClick = () => {
    track('Click primary engagement section title cta');
    navigate(primaryEngagementCard.title?.cta.to);
  };

  return (
    <div
      ref={containerRef}
      style={{
        border: '1px solid var(--color-yellow)',
        backgroundColor: 'rgba(255, 204, 0, 0.1)',
        borderRadius: 16,
        padding: '12px 16px 16px',
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          style={{
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: 12,
            color: 'var(--color-yellow)',
          }}
        >
          {primaryEngagementCard.title?.text}
        </div>
        {!!primaryEngagementCard.title?.cta && (
          <div
            className="d-flex align-items-center gap-2"
            onClick={handlePrimaryTitleCtaClick}
            style={{
              fontSize: 11,
              background: '#2c2412',
              borderRadius: 500,
              fontWeight: 600,
              padding: '4px 10px',
            }}
          >
            <div>{primaryEngagementCard.title.cta.text}</div>
            <div>
              <FontAwesomeIcon name="chevron-right" />
            </div>
          </div>
        )}
      </div>
      <PrimaryEngagementCardContent primaryEngagementCard={primaryEngagementCard} />
    </div>
  );
};
